import {useCallback, useEffect, useMemo, useState} from 'react';
import {Keyboard, Platform} from 'react-native';

import {SOCIAL_LINK_ORDER_BY_ID} from '../ManageMySite.constants';
import {SocialLink, SocialMediaApiPayload} from '../ManageMySite.types';
import {validateSocialLinks} from '../util/manageMySite.util';

type UseSocialLinksParams = {
  socialLinks: SocialLink[];
  onSave: (payload: SocialMediaApiPayload) => void;
};

const getInitialLinks = (socialLinks: SocialLink[]): SocialLink[] =>
  [...socialLinks].sort(
    (a, b) =>
      SOCIAL_LINK_ORDER_BY_ID.indexOf(a.socialNetworkID) -
      SOCIAL_LINK_ORDER_BY_ID.indexOf(b.socialNetworkID),
  );

export const useSocialLinks = ({socialLinks, onSave}: UseSocialLinksParams) => {
  const [links, setLinks] = useState<SocialLink[]>(() =>
    getInitialLinks(socialLinks),
  );
  const [errors, setErrors] = useState<Record<number, string | undefined>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [touchedFields, setTouchedFields] = useState<Record<number, boolean>>(
    {},
  );

  useEffect(() => {
    const showEvent =
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent =
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const showSub = Keyboard.addListener(showEvent, e => {
      setKeyboardHeight(e.endCoordinates.height);
    });
    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  const isDirty = useMemo(
    () =>
      links.some(link => {
        const original = socialLinks.find(
          item => item.socialNetworkID === link.socialNetworkID,
        );
        return (original?.url ?? '') !== link.url;
      }),
    [links, socialLinks],
  );

  const hasErrors = useMemo(
    () => Object.values(errors).some(Boolean),
    [errors],
  );

  const handleChange = useCallback((socialNetworkID: number, value: string) => {
    setLinks(prev =>
      prev.map(link =>
        link.socialNetworkID === socialNetworkID
          ? {
              ...link,
              url: value,
              resolvedUrl: value ? `${link.baseUrl}${value}` : '',
            }
          : link,
      ),
    );
    setErrors(prev => ({...prev, [socialNetworkID]: undefined}));
  }, []);

  const handleBlur = useCallback(
    (id: number) => {
      setTouchedFields(prev => ({...prev, [id]: true}));
      const link = links.find(l => l.socialNetworkID === id);
      if (!link) {
        return;
      }
      const newErrors = validateSocialLinks([link], socialLinks);
      setErrors(prev => ({
        ...prev,
        ...newErrors,
        ...(newErrors[id] === undefined ? {[id]: undefined} : {}),
      }));
    },
    [links, socialLinks],
  );
  const validate = useCallback(() => {
    const newErrors = validateSocialLinks(links, socialLinks);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [links, socialLinks]);

  const handleSave = useCallback(() => {
    setHasSubmitted(true);

    if (!validate()) {
      return;
    }

    const changedLinks = links.filter(link => {
      const original = socialLinks.find(
        item => item.socialNetworkID === link.socialNetworkID,
      );
      return (original?.url ?? '') !== link.url;
    });

    if (changedLinks.length === 0) {
      return;
    }

    const payload: SocialMediaApiPayload = {
      socialNetworks: changedLinks.map(link => ({
        socialNetworkID: link.socialNetworkID,
        url: link.url,
      })),
    };

    onSave(payload);
  }, [links, socialLinks, onSave, validate]);

  const resetState = useCallback(() => {
    setLinks(getInitialLinks(socialLinks));
    setErrors({});
    setTouchedFields({});
    setHasSubmitted(false);
  }, [socialLinks]);

  return {
    values: {
      links,
      errors,
      keyboardHeight,
      touchedFields,
    },
    derived: {
      isDirty,
      hasErrors,
      hasSubmitted,
    },
    actions: {
      handleChange,
      handleSave,
      resetState,
      handleBlur,
    },
  };
};
